import React, { useMemo } from 'react';
import { FaTrophy } from 'react-icons/fa';
import { IngredientPurchaseRecord } from '../../../../types';
import './ProviderRanking.scss';

interface ProviderRankingProps {
  purchases: IngredientPurchaseRecord[];
}

interface ProviderStat {
  name: string;
  totalSpent: number;
  purchaseCount: number;
  percentage: number;
}

const formatCurrency = (value: number) => 
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value);

const ProviderRanking: React.FC<ProviderRankingProps> = ({ purchases }) => {
  
  const providerStats = useMemo(() => {
    if (!purchases || purchases.length === 0) return [];

    // Group by provider
    const providerMap = new Map<string, { totalSpent: number; count: number }>();
    let totalSpent = 0;

    purchases.forEach(p => {
      const provider = p.provider_name || 'Sin Proveedor';
      const existing = providerMap.get(provider) || { totalSpent: 0, count: 0 };
      providerMap.set(provider, {
        totalSpent: existing.totalSpent + p.total_neto,
        count: existing.count + 1,
      });
      totalSpent += p.total_neto;
    });

    // Convert to array and calculate percentages
    const stats: ProviderStat[] = Array.from(providerMap.entries()).map(([name, data]) => ({
      name,
      totalSpent: data.totalSpent,
      purchaseCount: data.count,
      percentage: (data.totalSpent / totalSpent) * 100,
    }));

    // Sort by total spent descending
    stats.sort((a, b) => b.totalSpent - a.totalSpent);

    return stats.slice(0, 10); // Top 10
  }, [purchases]);

  if (providerStats.length === 0) {
    return null;
  }

  return (
    <div className="provider-ranking-wrapper">
      <div className="ranking-header">
        <h3><FaTrophy className="trophy-icon" /> Top Proveedores</h3>
        <p>Clasificación por gasto total</p>
      </div>
      
      <div className="ranking-list">
        {providerStats.map((provider, index) => (
          <div key={index} className={`ranking-item rank-${index + 1}`}>
            <div className="rank-badge">{index + 1}</div>
            
            <div className="provider-info">
              <div className="provider-name">{provider.name}</div>
              <div className="provider-purchases">{provider.purchaseCount} compras</div>
            </div>

            <div className="provider-stats">
              <div className="total-spent">{formatCurrency(provider.totalSpent)}</div>
              <div className="percentage-bar">
                <div 
                  className="percentage-fill" 
                  style={{ width: `${provider.percentage}%` }}
                />
              </div>
              <div className="percentage-text">{provider.percentage.toFixed(1)}%</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProviderRanking;
